'use client';

import * as React from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { faCircleChevronLeft, faCircleChevronRight } from '@fortawesome/pro-light-svg-icons';
import { tv } from 'tailwind-variants';

import { DotButton, useDotButton, DotsVariantEnum } from './carousel-dot-button.component';
import { Icon } from './icon.component';

import { cn } from '@/utils/cn-tw-merge';
import { Button } from '@/components/ui/button/button.component';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  margin?: number;
  showDots?: boolean;
  showArrows?: boolean;
  arrowsPosition?: 'top' | 'bottom' | 'center';
  hideDotsWhenCantScroll?: boolean;
  arrowsClassName?: string;
  dotsClassName?: string;
  setApi?: (api: CarouselApi) => void;
  noArrowsGap?: boolean;
  dotsColorVariant?: DotsVariantEnum;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const carouselStyles = tv({
  slots: {
    arrow: '',
    dotsContainer: 'flex flex-wrap justify-center align-center gap-2 mt-4',
  },

  variants: {
    arrowsPosition: {
      center: {
        arrow: 'top-1/2 -translate-y-1/2',
      },

      top: {
        arrow: 'top-5',
      },

      bottom: {
        arrow: 'bottom-4',
      },
    },

    shouldShowDots: {
      false: {
        dotsContainer: 'hidden',
      },
    },

    showArrows: {
      false: {
        arrow: 'hidden',
      },
    },
  },

  defaultVariants: {
    arrowsPosition: 'center',
  },
});

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      showDots,
      showArrows,
      arrowsPosition = 'center',
      children,
      hideDotsWhenCantScroll,
      arrowsClassName,
      dotsClassName,
      noArrowsGap,
      dotsColorVariant = DotsVariantEnum.GRAY,
      ...props
    },
    ref,
  ) => {
    const [options, setOptions] = React.useState<CarouselOptions>({
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    });
    const [carouselRef, api] = useEmblaCarousel(options, plugins);

    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const carouselContainerRef = ref as React.RefObject<HTMLDivElement>;
    const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);

    const { arrow, dotsContainer } = carouselStyles();

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      if (!api) {
        return;
      }

      const isFirstSlide = api.selectedScrollSnap() === 0;
      if (isFirstSlide) {
        api.scrollTo(api.scrollSnapList().length - 1);
      } else {
        api.scrollPrev();
      }
    }, [api]);

    const scrollNext = React.useCallback(() => {
      if (!api) {
        return;
      }

      const isLastSlide = api.selectedScrollSnap() === api.scrollSnapList().length - 1;
      if (isLastSlide) {
        api.scrollTo(0);
      } else {
        api.scrollNext();
      }
    }, [api]);

    const canScroll = canScrollPrev || canScrollNext;

    const shouldShowDots = showDots && (canScroll || !hideDotsWhenCantScroll);

    const shouldShowArrows = scrollSnaps.length > 1;

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    React.useEffect(() => {
      if (!api || !opts) {
        return;
      }

      setOptions(opts);
    }, [opts, api]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          canScrollPrev,
          canScrollNext,
          scrollPrev,
          scrollNext,
        }}
      >
        <div
          ref={carouselContainerRef}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}

          {shouldShowArrows && (
            <>
              <CarouselPrevious
                noArrowsGap={noArrowsGap}
                className={arrow({ arrowsPosition, className: arrowsClassName, showArrows })}
              />
              <CarouselNext
                noArrowsGap={noArrowsGap}
                className={arrow({ arrowsPosition, className: arrowsClassName, showArrows })}
              />
            </>
          )}

        </div>
        <div className={dotsContainer({ className: dotsClassName, shouldShowDots })}>
          {scrollSnaps
            && scrollSnaps.map((_, index) => (
              <DotButton
                key={`key-dots-${index}`}
                onClick={() => onDotButtonClick(index)}
                selected={index === selectedIndex}
                variant={dotsColorVariant}
              />
            ))}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

type CarouselContentProps = {
  overflowClassName?: string;
  endRef?: React.RefObject<HTMLDivElement | null>;
};

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselContentProps>(
  ({ className, overflowClassName, endRef, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div ref={carouselRef} className={cn('overflow-hidden', overflowClassName)}>
        <div
          ref={ref}
          className={cn('flex', orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col', className)}
          {...props}
        />
        {endRef && <div ref={endRef} />}
      </div>
    );
  },
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn('min-w-0 shrink-0 grow-0', orientation === 'horizontal' ? 'pl-3' : 'pt-3', className)}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

interface ArrowProps {
  noArrowsGap?: boolean;
}

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button> & ArrowProps>(
  ({ className, variant = 'outline', noArrowsGap, ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant as 'default' | 'secondary' | 'text' | undefined}
        className={cn(
          'absolute h-[1.875rem] w-[1.875rem] rounded-full border-none',
          orientation === 'horizontal'
            ? noArrowsGap
              ? '-left-[1.8rem] top-1/2 -translate-y-1/2'
              : '-left-[2.625rem] top-1/2 -translate-y-1/2'
            : '-top-11 left-1/2 -translate-x-1/2 rotate-90',
          className,
          !canScrollPrev && 'opacity-50 pointer-events-none',
        )}
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        {...props}
      >
        <Icon icon={faCircleChevronLeft} className="h-[1.875rem] w-[1.875rem] text-3xl text-gray-500 bg-transparent" />

        <span className="sr-only">Previous slide</span>
      </Button>
    );
  },
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button> & ArrowProps>(
  ({ className, variant = 'outline', noArrowsGap, ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant as 'default' | 'secondary' | 'text' | undefined}
        className={cn(
          'absolute h-[1.875rem] w-[1.875rem] rounded-full border-none',
          orientation === 'horizontal'
            ? noArrowsGap
              ? '-right-[1.8rem] top-1/2 -translate-y-1/2'
              : '-right-[2.625rem] top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          !canScrollNext && 'opacity-50 pointer-events-none',
          className,
        )}
        onClick={scrollNext}
        disabled={!canScrollNext}
        {...props}
      >
        <Icon icon={faCircleChevronRight} className="h-[1.875rem] w-[1.875rem] text-3xl text-gray-500 bg-transparent" />
        <span className="sr-only">Next slide</span>
      </Button>
    );
  },
);
CarouselNext.displayName = 'CarouselNext';

export { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };
